import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, XCircle, AlertCircle, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';

export interface ImportResult {
  ticketNumber: string;
  status: 'success' | 'failed' | 'warning';
  message?: string;
  assignedTo?: string;
  vehicleInfo?: string;
}

export interface ImportReportData {
  totalTickets: number;
  successful: number;
  failed: number;
  warnings: number;
  results: ImportResult[];
  duration: number;
  timestamp: Date;
}

interface ImportReportProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  report: ImportReportData | null;
}

export const ImportReport = ({ open, onOpenChange, report }: ImportReportProps) => {
  if (!report) return null;

  const successRate = ((report.successful / report.totalTickets) * 100).toFixed(1);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <CheckCircle2 className="h-6 w-6 text-primary" />
            Import Complete
          </DialogTitle>
          <DialogDescription className="text-base">
            Import completed on {report.timestamp.toLocaleString()}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-4 gap-4 py-6">
          <div className="text-center p-4 bg-muted rounded-lg">
            <div className="text-3xl font-bold text-foreground">{report.totalTickets}</div>
            <div className="text-sm text-muted-foreground mt-1">Total</div>
          </div>
          <div className="text-center p-4 bg-success/10 rounded-lg border border-success/20">
            <div className="text-3xl font-bold text-success">{report.successful}</div>
            <div className="text-sm text-muted-foreground mt-1">Success</div>
          </div>
          <div className="text-center p-4 bg-destructive/10 rounded-lg border border-destructive/20">
            <div className="text-3xl font-bold text-destructive">{report.failed}</div>
            <div className="text-sm text-muted-foreground mt-1">Failed</div>
          </div>
          <div className="text-center p-4 bg-warning/10 rounded-lg border border-warning/20">
            <div className="text-3xl font-bold text-warning">{report.warnings}</div>
            <div className="text-sm text-muted-foreground mt-1">Warnings</div>
          </div>
        </div>

        <div className="flex items-center justify-between text-sm border-y py-4 bg-muted/30">
          <div className="flex items-center gap-2 px-2">
            <Clock className="h-5 w-5 text-muted-foreground" />
            <span className="font-medium">Duration: {(report.duration / 1000).toFixed(1)}s</span>
          </div>
          <div className="font-semibold text-base px-2">
            Success Rate: {successRate}%
          </div>
        </div>

        <ScrollArea className="h-[400px] pr-4">
          <div className="space-y-3">
            {report.results.map((result, idx) => (
              <div
                key={idx}
                className="flex items-start gap-3 p-4 border rounded-lg hover:bg-muted/50 transition-colors"
              >
                <div className="mt-1">
                  {result.status === 'success' && (
                    <CheckCircle2 className="h-5 w-5 text-success" />
                  )}
                  {result.status === 'failed' && (
                    <XCircle className="h-5 w-5 text-destructive" />
                  )}
                  {result.status === 'warning' && (
                    <AlertCircle className="h-5 w-5 text-warning" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-semibold text-base">{result.ticketNumber}</span>
                    <Badge variant={
                      result.status === 'success' ? 'default' :
                      result.status === 'failed' ? 'destructive' : 'secondary'
                    } className="text-xs">
                      {result.status}
                    </Badge>
                  </div>
                  {result.vehicleInfo && (
                    <div className="text-sm text-muted-foreground mb-1">
                      {result.vehicleInfo}
                    </div>
                  )}
                  {result.assignedTo && (
                    <div className="text-sm text-muted-foreground mb-1">
                      Assigned to: {result.assignedTo}
                    </div>
                  )}
                  {result.message && (
                    <div className="text-sm text-muted-foreground">
                      {result.message}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
